import { SessionKindGroupName, SessionPreviewPointDto } from 'types/models/SessionsPageData';
import { soccerZToChartZ } from 'pages/MeasurementDetail/coords';

const clampPct = (value: number): number => Math.max(0, Math.min(100, value));

const TOP_Z_MIN = -34;
const TOP_Z_MAX = 34;
const TOP_X_MIN = -3;
const TOP_X_MAX = 58;

const GOAL_WIDTH = 7.32;
const GOAL_HEIGHT = 2.44;
const GOAL_MARGIN_SIDE = 1.5;
const GOAL_MARGIN_TOP = 1.0;
const GOAL_MARGIN_BOTTOM = 0.3;

const inTopBounds = (z: number, x: number): boolean => (
  z >= TOP_Z_MIN && z <= TOP_Z_MAX && x >= TOP_X_MIN && x <= TOP_X_MAX
);

export const mapTopPreviewPoint = (
  measurement: SessionPreviewPointDto,
  kind: SessionKindGroupName,
): { xPct: number; yPct: number } | null => {
  const useLanding = kind === 'Goalkeeper' || kind === 'ThrowIn';
  const lateralRaw = useLanding
    ? (measurement.landingZ ?? measurement.kickZ)
    : measurement.kickZ;
  const depth = useLanding
    ? (measurement.landingX ?? measurement.kickX)
    : measurement.kickX;
  if (lateralRaw === null || depth === null) return null;

  const lateral = soccerZToChartZ(lateralRaw);
  const xPct = clampPct(((lateral - TOP_Z_MIN) / (TOP_Z_MAX - TOP_Z_MIN)) * 100);
  const yPct = clampPct(((depth - TOP_X_MIN) / (TOP_X_MAX - TOP_X_MIN)) * 100);
  return { xPct, yPct };
};

// NOTE: duplicated with SessionsPitchChart.tsx (toPitchPoint) — coordinate-normalization logic overlaps
// but differs in bounds/output units; intentionally not merged (too risky to verify).
export const mapTopPreviewReachablePoint = (
  coords: [number, number] | null,
  measurement: SessionPreviewPointDto,
): { xPct: number; yPct: number } | null => {
  if (!coords || coords.length < 2) return null;

  const candidates: [number, number][] = [
    [soccerZToChartZ(coords[1]), coords[0]],
    [soccerZToChartZ(coords[0]), coords[1]],
  ];

  const inBounds = candidates.find((c) => inTopBounds(c[0], c[1]));
  let chosen = inBounds ?? null;

  if (!chosen && measurement.kickX !== null && measurement.kickZ !== null) {
    const kickZ = soccerZToChartZ(measurement.kickZ);
    const kickX = measurement.kickX;
    chosen = candidates
      .map((candidate) => ({
        candidate,
        d2: ((candidate[0] - kickZ) ** 2) + ((candidate[1] - kickX) ** 2),
      }))
      .sort((a, b) => a.d2 - b.d2)[0]?.candidate ?? null;
  }

  if (!chosen) chosen = candidates[0] ?? null;
  if (!chosen) return null;

  const xPct = clampPct(((chosen[0] - TOP_Z_MIN) / (TOP_Z_MAX - TOP_Z_MIN)) * 100);
  const yPct = clampPct(((chosen[1] - TOP_X_MIN) / (TOP_X_MAX - TOP_X_MIN)) * 100);
  return { xPct, yPct };
};

export const mapGoalPreviewPoint = (
  measurement: SessionPreviewPointDto,
): { xPct: number; yPct: number } | null => {
  if (measurement.goalY === null || measurement.goalZ === null) return null;

  const halfGoalWidth = GOAL_WIDTH / 2;
  const zMin = -(halfGoalWidth + GOAL_MARGIN_SIDE);
  const zMax = halfGoalWidth + GOAL_MARGIN_SIDE;
  const yMin = -GOAL_MARGIN_BOTTOM;
  const yMax = GOAL_HEIGHT + GOAL_MARGIN_TOP;

  const chartZ = soccerZToChartZ(measurement.goalZ);
  const xPct = clampPct(((chartZ - zMin) / (zMax - zMin)) * 100);
  const yPct = clampPct(100 - (((measurement.goalY - yMin) / (yMax - yMin)) * 100));
  return { xPct, yPct };
};
